import { Component, Prop, State, Element, h, Fragment } from '@stencil/core';
import { b2bCalendarImage } from '../../models/stores/b2bCalendarImage';
import { ComponentColors, initColors } from '../../utils/constants/colors';
import { z } from 'zod';
import { TranslationsProps } from '../../utils/services/translationService';

export const MainImageStyle = z.enum(['default', 'cover', 'fullWidth', 'fullHeight']);
export type MainImageStyle = z.infer<typeof MainImageStyle>;
export const BgImageStyle = z.enum(['default', 'glass', 'hidden']);
export type BgImageStyle = z.infer<typeof BgImageStyle>;
export const ButtonPosition = z.enum([
  'default', 'onMainImage', 'nextToIndicators', 'hidden',
]);
export type ButtonPosition = z.infer<typeof ButtonPosition>;
export const IndicatorPosition = z.enum([
  'default', 'under', 'hidden',
]);
export type IndicatorPosition = z.infer<typeof IndicatorPosition>;

export const ASL_ImageCarouselProps = z.object({
  mainImageStyle: MainImageStyle.optional(),
  backgroundImageStyle: BgImageStyle.optional(),
  buttonPosition: ButtonPosition.optional(),
  indicatorPosition: IndicatorPosition.optional(),

  isAutoPlayEnabled: z.boolean().optional(),
  autoPlayDelay: z.number().optional(),
  isAutoPlayResumable: z.boolean().optional(),
  autoPlayResumeDelay: z.number().optional(),
  componentColors: ComponentColors.optional(),
  translationsProps: TranslationsProps.optional(),
});
export type ASL_ImageCarouselProps = z.infer<typeof ASL_ImageCarouselProps>;


@Component({
  tag: 'aico-store-locator-image-carousel',
  styleUrl: 'aico-store-locator-image-carousel.css',
  shadow: true,
})
export class AicoStoreLocatorImageCarousel {
  @Prop({ mutable: true }) images: b2bCalendarImage[] = [];

  @Prop() mainImageStyle: MainImageStyle = 'default';
  @Prop() backgroundImageStyle: BgImageStyle = 'default';
  @Prop() buttonPosition: ButtonPosition = 'default';
  @Prop() indicatorPosition: IndicatorPosition = 'default';


  @Prop() isAutoPlayEnabled: boolean = true;
  @Prop() autoPlayDelay: number = 2000;
  @Prop() isAutoPlayResumable: boolean = true;
  @Prop() autoPlayResumeDelay: number = 5000;
  @Prop() componentColors?: ComponentColors;
  @Prop() props?: ASL_ImageCarouselProps;

  @State() currentIndex: number = 0;
  @State() isAutoPlayPaused: boolean = false;

  private autoPlayInterval?: NodeJS.Timeout;
  private autoPlayResumeTimeout?: NodeJS.Timeout;

  @Element() el!: HTMLElement;

  componentWillLoad() {
    if (this.props) {
      const parsedProps = ASL_ImageCarouselProps.parse(this.props);
      this.mainImageStyle = parsedProps.mainImageStyle ?? this.mainImageStyle;
      this.backgroundImageStyle = parsedProps.backgroundImageStyle ?? this.backgroundImageStyle;
      this.buttonPosition = parsedProps.buttonPosition ?? this.buttonPosition;
      this.indicatorPosition = parsedProps.indicatorPosition ?? this.indicatorPosition;

      this.isAutoPlayEnabled = parsedProps.isAutoPlayEnabled ?? this.isAutoPlayEnabled;
      this.autoPlayDelay = parsedProps.autoPlayDelay ?? this.autoPlayDelay;
      this.isAutoPlayResumable = parsedProps.isAutoPlayResumable ?? this.isAutoPlayResumable;
      this.autoPlayResumeDelay = parsedProps.autoPlayResumeDelay ?? this.autoPlayResumeDelay;
      this.componentColors = parsedProps.componentColors ?? this.componentColors;
    }

    if (this.componentColors) {
      this.componentColors = ComponentColors.parse(this.componentColors);
      initColors(this.el, this.componentColors);
    }

    if (!this.images) {
      this.images = [];
      return;
    }
    this.images = [...this.images].sort((a, b) => a.sortOrder - b.sortOrder);
  }

  componentDidLoad() { this.startAutoPlay(); }

  disconnectedCallback() {
    this.stopAutoPlay();
    if (this.autoPlayResumeTimeout) clearTimeout(this.autoPlayResumeTimeout);
  }

  private startAutoPlay() {
    if (!this.isAutoPlayPaused && this.isAutoPlayEnabled && this.images.length > 1) {
      this.stopAutoPlay();
      this.autoPlayInterval = setInterval(() => this.nextImage(), this.autoPlayDelay);
    }
  }
  private stopAutoPlay() {
    if (this.autoPlayInterval) clearInterval(this.autoPlayInterval);
  }
  private pauseAutoPlay() {
    this.isAutoPlayPaused = true;
    this.stopAutoPlay();
    if (this.autoPlayResumeTimeout) clearTimeout(this.autoPlayResumeTimeout);

    if (this.isAutoPlayResumable) {
      this.autoPlayResumeTimeout = setTimeout(() => {
        this.isAutoPlayPaused = false; this.startAutoPlay();
      }, this.autoPlayResumeDelay);
    }
  }

  private previousImage() { this.pauseAutoPlay(); this.currentIndex = (this.currentIndex - 1 + this.images.length) % this.images.length; }
  private nextImage() { this.pauseAutoPlay(); this.currentIndex = (this.currentIndex + 1) % this.images.length; }

  private get mainImgClass() {
    switch (this.mainImageStyle) {
      case 'cover':
        return 'rounded-md w-full h-full object-cover';
      case 'fullWidth':
        return 'rounded-md w-full object-cover';
      case 'fullHeight':
        return 'rounded-md h-full object-cover';
      default:
        return 'rounded-md w-full h-full object-contain';
    }
  }

  private get bgImgClass() {
    switch (this.backgroundImageStyle) {
      case 'glass':
        return 'rounded-md opacity-10 backdrop-blur-sm bg-white';
      case 'hidden':
        return 'hidden';
      default:
        return 'rounded-md opacity-20 blur-sm';
    }
  }

  private get navBase() {
    const tiny = this.buttonPosition === 'nextToIndicators';
    return [
      'absolute z-20 transition',
      tiny
        ? 'p-1 text-[--primary-button-text-color]'
        : 'p-2 rounded-full bg-black/50 hover:bg-black/70 text-white',
    ].join(' ');
  }

  private get indicatorWrapper() {
    return 'absolute inset-x-0 bottom-4 flex justify-center z-20';
  }

  render() {
    if (this.images.length === 0) {
      return (
        <div class="flex justify-center items-center bg-[--background-color] rounded-lg w-full h-full" />
      );
    }

    const img = this.images[this.currentIndex];
    const underBar = this.indicatorPosition === 'under';
    const showDots = this.indicatorPosition !== 'hidden';
    const dotsInside = showDots && !underBar;
    const onImage = this.buttonPosition === 'onMainImage';
    const withDots = this.buttonPosition === 'nextToIndicators';
    const overlayArrows = this.images.length > 1 && this.buttonPosition === 'default';
    const dotChevrons = this.images.length > 1 && withDots;

    const dotsBar = (
      <div class="flex items-center gap-2">
        {dotChevrons && (
          <button aria-label="Prev"
            class="p-1 text-[--primary-text-color]"
            onClick={() => this.previousImage()}>
            <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none"
              viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}

        {/* dots themselves */}
        {this.images.map((_, idx) => (
          <button key={idx}
            aria-label={`Go to image ${idx + 1}`}
            class={[
              'h-2.5 w-2.5 rounded-full transition',
              idx === this.currentIndex
                ? 'bg-[--indicator-selected-color]'
                : 'bg-[--indicator-default-color] hover:bg-[--indicator-hover-color]',
            ].join(' ')}
            onClick={() => { this.pauseAutoPlay(); this.currentIndex = idx; }}
          />
        ))}

        {dotChevrons && (
          <button aria-label="Next"
            class="p-1 text-[--primary-text-color]"
            onClick={() => this.nextImage()}>
            <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none"
              viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}
      </div>
    );

    return (
      <div class={`w-full h-full ${underBar ? 'flex flex-col' : ''}`}>
        <div class={[
          'relative w-full rounded-lg',
          underBar ? 'flex-1' : 'h-full',
        ].join(' ')}>

          {this.backgroundImageStyle !== 'hidden' && (
            <img src={img.imageUrl} alt=""
              class={`${this.bgImgClass} absolute inset-0 h-full w-full object-cover`} />
          )}

          <div class="absolute inset-0">
            <div class={`relative flex justify-center items-center rounded-lg w-full h-full ${this.mainImageStyle !== 'default' ? 'overflow-hidden' : ''}`}>
              <img src={img.imageUrl}
                alt={`Image ${this.currentIndex + 1}`}
                class={this.mainImgClass} />

              {onImage && (
                <Fragment>
                  <button aria-label="Prev"
                    class="top-1/2 left-2 absolute bg-black/50 hover:bg-black/70 p-2 rounded-full text-white -translate-y-1/2"
                    onClick={() => this.previousImage()}>
                    <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6" fill="none"
                      viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>

                  <button aria-label="Next"
                    class="top-1/2 right-2 absolute bg-black/50 hover:bg-black/70 p-2 rounded-full text-white -translate-y-1/2"
                    onClick={() => this.nextImage()}>
                    <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6" fill="none"
                      viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </Fragment>
              )}
            </div>
          </div>

          {overlayArrows && (
            <Fragment>
              <button aria-label="Prev"
                class={`${this.navBase} left-4 top-1/2 -translate-y-1/2`}
                onClick={() => this.previousImage()}>
                <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6" fill="none"
                  viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              <button aria-label="Next"
                class={`${this.navBase} right-4 top-1/2 -translate-y-1/2`}
                onClick={() => this.nextImage()}>
                <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6" fill="none"
                  viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </Fragment>
          )}

          {dotsInside && <div class={this.indicatorWrapper}>{dotsBar}</div>}
        </div>

        {underBar && (
          <div class="flex flex-none justify-center items-center mt-1 h-7">
            {dotsBar}
          </div>
        )}
      </div>
    );
  }

}
